import {
  Box,
  FormControl,
  OutlinedInput,
  FormHelperText,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FC, useState, MouseEvent, ChangeEvent } from 'react';
import { TextFieldProps } from 'src/models/FieldProps.interface';
import { regularExpressions } from 'src/utils/properties.utils';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import { unitErrorTextStyles } from '../StyledComponents/StyledUnitPicker';
import { StyledErrorTooltip } from './TextField.styles';

const TextField: FC<TextFieldProps> = ({
  id,
  label,
  color = 'var(--primary-navy)',
  type = 'text',
  numberOnly = false,
  showPasswordToggle = false,
  showPasswordColor = 'var(--primary-blue)',
  formikProps,
  sx,
  errorState = false,
  isEditingOwnAccount,
  height = '40px',
  width = 'min(450px, 100%)',
  disabled,
  placeholder = '',
  showLabel = true,
  onChange,
  mb,
  value,
  errorTooltiplength,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const fieldProps = formikProps.getFieldProps(id);
  const fieldMeta = formikProps.getFieldMeta(id);

  const handleEditClick = () => {
    setIsEditable(!isEditable);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let lValue = e.target.value;

    if (id === 'firstName' || id === 'lastName') {
      lValue = lValue.replace(regularExpressions.NAME_REGEX, '');
    }

    if (numberOnly) {
      lValue = lValue.replace(regularExpressions.NUMBER_ONLY_REGEX, '');
      const parts = lValue.split('.');
      if (parts.length > 2) {
        lValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    if (onChange) {
      onChange(e);
    } else {
      formikProps.handleChange({
        target: {
          name: id,
          value: lValue,
        },
      });
    }
  };
  return (
    <Box mb={mb || (showLabel ? 3 : 0)}>
      {showLabel && (
        <Typography
          fontFamily="Outfit"
          fontWeight="400"
          fontSize="16px"
          color={color}
          mb={2}
        >
          {label}
        </Typography>
      )}
      <FormControl
        fullWidth
        error={
          ((fieldMeta.touched || formikProps.submitCount > 0) &&
            !!fieldMeta.error) ||
          errorState
        }
        sx={{ m: 0 }}
      >
        <OutlinedInput
          id={id}
          type={showPasswordToggle && showPassword ? 'text' : type}
          value={value ?? fieldProps.value ?? ''}
          onChange={handleChange}
          placeholder={placeholder}
          inputProps={{
            readOnly:
              isEditingOwnAccount ||
              disabled ||
              (type === 'edit' && !isEditable),
            style: {
              textTransform: id === 'customerCode' ? 'uppercase' : 'none',
            },
          }}
          startAdornment={
            type === 'edit' ? (
              <InputAdornment position="start">
                <Box
                  component="i"
                  className="bx bx-edit-alt 1"
                  sx={{
                    fontSize: '16px',
                    color: 'var(--instructions)',
                    cursor: 'pointer',
                  }}
                  onClick={handleEditClick}
                />
              </InputAdornment>
            ) : null
          }
          endAdornment={
            showPasswordToggle ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  sx={{ color: showPasswordColor }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : null
          }
          sx={{
            height: height,
            width: width,
            bgcolor:
              type === 'edit' && !isEditable ? 'transparent' : 'var(--text-bg)',
            border: 'none',
            borderRadius: 1,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor:
                type === 'edit' && isEditable === false
                  ? 'transparent'
                  : 'var(--border)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor:
                type === 'edit' && isEditable === false
                  ? 'transparent'
                  : 'var(--primary-blue)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor:
                type === 'edit' && isEditable === false
                  ? 'transparent'
                  : 'var(--primary-blue)',
            },
            '& input': {
              fontFamily: 'Outfit',
              fontSize: '16px',
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
            '& input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            overflow: 'hidden',
            '& input:-webkit-autofill': {
              bgcolor: 'var(--bg-color)',
              WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-moz-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-ms-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            ...((fieldMeta.touched || formikProps.submitCount > 0) &&
              Boolean(fieldMeta.error) && {
                borderColor: 'var(--error)',
              }),
            ...(errorState && {
              borderColor: 'var(--error)',
            }),
            ...sx,
          }}
        />
        {(fieldMeta.touched || formikProps.submitCount > 0) &&
          fieldMeta.error && (
            <>
              {errorTooltiplength &&
              fieldMeta.error.length > errorTooltiplength ? (
                <StyledErrorTooltip
                  title={fieldMeta.error}
                  arrow
                  placement="top"
                >
                  <FormHelperText
                    sx={{
                      ...unitErrorTextStyles,
                      cursor: 'pointer',
                    }}
                  >
                    <ErrorIcon />
                    {fieldMeta.error.substring(0, errorTooltiplength) + '...'}
                  </FormHelperText>
                </StyledErrorTooltip>
              ) : (
                <FormHelperText sx={{ ...unitErrorTextStyles }}>
                  <ErrorIcon />
                  {fieldMeta.error}
                </FormHelperText>
              )}
            </>
          )}
      </FormControl>
    </Box>
  );
};

export default TextField;
